import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useClientePedidos } from '../../hooks/useClientePedidos';
import { PedidoDetalle, estadosPedido } from '../../components/orders/PedidoDetalle';
import type { PedidoResponse } from '../../services/orders/orders.service';
import {
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
  Search,
  MapPin,
  AlertCircle,
  User,
  Loader2,
  ArrowLeft,
} from 'lucide-react';

const estadosActivos = ['PENDING_PAYMENT', 'PAID', 'PROCESSING', 'SHIPPED'];

export const SeguimientoPedidosPage: React.FC = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const { pedidos, loading, error } = useClientePedidos();
  const [busqueda, setBusqueda] = useState('');
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState<PedidoResponse | null>(null);

  const esSeguimiento = location.pathname === '/cliente/seguimiento';

  const pedidosFiltrados = pedidos.filter((pedido) => {
    const matchBusqueda =
      pedido.numeroPedido.toLowerCase().includes(busqueda.toLowerCase()) ||
      pedido.cliente.nombre.toLowerCase().includes(busqueda.toLowerCase());
    const matchEstado = esSeguimiento ? estadosActivos.includes(pedido.estado) : true;
    return matchBusqueda && matchEstado;
  });

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'PENDING_PAYMENT':
        return <Clock className="w-4 h-4" />;
      case 'PAID':
        return <CreditCard className="w-4 h-4" />;
      case 'PROCESSING':
        return <Package className="w-4 h-4" />;
      case 'SHIPPED':
        return <Truck className="w-4 h-4" />;
      case 'DELIVERED':
        return <CheckCircle className="w-4 h-4" />;
      case 'CANCELLED':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const getEstadoColorClasses = (estado: string) => {
    switch (estado) {
      case 'PENDING_PAYMENT':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'PAID':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PROCESSING':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'SHIPPED':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'DELIVERED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEstadoLabel = (estado: string) => estadosPedido[estado]?.label || estado;

  return (
    <div className="p-6 flex flex-col gap-4 h-[calc(100vh-5rem)]">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-shrink-0">
        <div>
          <h1 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {esSeguimiento ? 'Seguimiento de Pedidos' : 'Mis Pedidos'}
          </h1>
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {esSeguimiento
              ? 'Rastrea el estado de tus pedidos'
              : 'Consulta todos tus pedidos'}
          </p>
        </div>
        <div className={`px-3 py-1 rounded-md text-xs font-medium ${
          theme === 'dark' ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
        }`}>
          {pedidosFiltrados.length} pedido{pedidosFiltrados.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Búsqueda */}
      <div className="flex-shrink-0 flex flex-col lg:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por número de pedido..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-xl border-2 transition-all duration-300 ${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-emerald-500'
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-emerald-500'
            }`}
          />
        </div>
      </div>

      {/* Contenido scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            <span className={`ml-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Cargando pedidos...
            </span>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${
            theme === 'dark' ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200'
          }`}>
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className={theme === 'dark' ? 'text-red-300' : 'text-red-700'}>{error}</p>
          </div>
        )}

        {/* Grid lista + detalle */}
        {!loading && !error && (
          <div className={`grid grid-cols-1 gap-6 ${pedidoSeleccionado ? 'xl:grid-cols-3' : 'lg:grid-cols-2'}`}>
            {/* Lista de pedidos */}
            <div className="space-y-4">
              {pedidosFiltrados.map((pedido) => (
                <div
                  key={pedido.id}
                  onClick={() => setPedidoSeleccionado(pedido)}
                  className={`p-5 rounded-xl border cursor-pointer transition-all duration-300 ${
                    pedidoSeleccionado?.id === pedido.id
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : theme === 'dark'
                        ? 'bg-gray-800 border-gray-700 hover:border-gray-600'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {pedido.numeroPedido}
                      </h3>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {new Date(pedido.fechas.creadoEn).toLocaleDateString()}
                      </p>
                    </div>
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm border ${getEstadoColorClasses(pedido.estado)}`}>
                      {getEstadoIcon(pedido.estado)}
                      <span>{getEstadoLabel(pedido.estado)}</span>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {pedido.cliente.nombre}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {pedido.envio.direccion}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <span className={`text-lg font-semibold ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        ${pedido.totales.total.toLocaleString('es-CO')}
                      </span>
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {pedido.items.length} producto{pedido.items.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {pedidosFiltrados.length === 0 && (
                <div className={`text-center py-12 rounded-xl border-2 border-dashed ${
                  theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
                }`}>
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No se encontraron pedidos</p>
                  <p className="mt-1">
                    {esSeguimiento
                      ? 'No tienes pedidos activos en este momento'
                      : 'Aún no has realizado ningún pedido'}
                  </p>
                </div>
              )}
            </div>

            {/* Detalle del pedido seleccionado */}
            <div className={pedidoSeleccionado ? 'xl:col-span-2' : ''}>
              {pedidoSeleccionado ? (
                <div className={`rounded-xl border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="p-4">
                    <PedidoDetalle
                      pedido={pedidoSeleccionado}
                      onPedidoChange={setPedidoSeleccionado}
                      headerAction={
                        <button
                          onClick={() => setPedidoSeleccionado(null)}
                          className={`inline-flex items-center gap-1 text-xs hover:underline ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}
                        >
                          <ArrowLeft className="w-3 h-3" />
                          Volver a la lista
                        </button>
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className={`text-center py-12 rounded-xl border ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  <Package className={`w-12 h-12 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Selecciona un pedido
                  </p>
                  <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Haz clic en cualquier pedido para ver el seguimiento completo
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
